import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
  useFormState,
} from 'react-hook-form';

import type { z } from 'zod';

import { cn } from '../lib/utils';
import { Label } from './label';

/**
 * Schema plumbing re-exports — modules should pull `zodResolver` and
 * `FormSchema` from here (or the ui-kit barrel) instead of importing
 * `@hookform/resolvers/zod` directly, so the resolver version stays pinned
 * to the `react-hook-form` the primitives are built against.
 *
 *   const schema = z.object({ name: z.string().min(1) });
 *   type Values = FormSchema<typeof schema>;
 *   const form = useForm<Values>({ resolver: zodResolver(schema) });
 */
export { zodResolver } from '@hookform/resolvers/zod';

/** Infers the form values type from a zod schema. */
export type FormSchema<TSchema extends z.ZodTypeAny> = z.infer<TSchema>;

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id, renderedSlots, registerSlot } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    /** Which of the two describing elements are actually in the document. */
    renderedSlots,
    registerSlot,
    ...fieldState,
  };
};

/** The describing elements a FormItem can hold. */
type FormSlot = 'description' | 'message';

type FormItemContextValue = {
  id: string;
  /**
   * WCAG 4.1.2 / axe `aria-valid-attr-value`. `FormControl` used to emit
   * `aria-describedby="<id>-form-item-description"` unconditionally, and
   * `<FormDescription>` is optional — 122 of the 250 module files that render
   * a `FormControl` never render one, and `FormMessage` returns null whenever
   * the resolved error carries no message. Every one of those controls
   * advertised a description at an element id that does not exist, which a
   * screen reader follows to nothing.
   *
   * `field.tsx` already solved this for the non-react-hook-form path with
   * `useRegisterFieldSlot`; this is the same contract for the RHF path:
   * the describing elements register while they are mounted, and the control
   * references only the ids that registered.
   */
  renderedSlots: ReadonlySet<FormSlot>;
  registerSlot: (slot: FormSlot, present: boolean) => void;
};

const NO_SLOTS: ReadonlySet<FormSlot> = new Set<FormSlot>();

const FormItemContext = React.createContext<FormItemContextValue>({
  id: '',
  renderedSlots: NO_SLOTS,
  registerSlot: () => undefined,
});

/**
 * Registers `slot` with the enclosing FormItem for exactly as long as
 * `present` holds. Returns the id the element must carry.
 */
function useRegisterFormSlot(slot: FormSlot, present: boolean): void {
  const { registerSlot } = React.useContext(FormItemContext);
  React.useEffect(() => {
    if (!present) return;
    registerSlot(slot, true);
    return () => registerSlot(slot, false);
  }, [registerSlot, slot, present]);
}

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId();
  const [renderedSlots, setRenderedSlots] = React.useState<ReadonlySet<FormSlot>>(NO_SLOTS);

  const registerSlot = React.useCallback((slot: FormSlot, present: boolean) => {
    setRenderedSlots((previous) => {
      if (previous.has(slot) === present) return previous;
      const next = new Set(previous);
      if (present) next.add(slot);
      else next.delete(slot);
      return next;
    });
  }, []);

  const value = React.useMemo(
    () => ({ id, renderedSlots, registerSlot }),
    [id, renderedSlots, registerSlot]
  );

  return (
    <FormItemContext.Provider value={value}>
      <div ref={ref} className={cn('space-y-2', className)} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && 'text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId, renderedSlots } =
    useFormField();

  // Reference ONLY the describing elements that registered. A control with
  // neither drops the attribute entirely rather than pointing at a missing id.
  const describedBy = [
    renderedSlots.has('description') ? formDescriptionId : null,
    renderedSlots.has('message') ? formMessageId : null,
  ]
    .filter((token): token is string => token !== null)
    .join(' ');

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();
  useRegisterFormSlot('description', true);

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : children;
  // Registered only while a message is actually rendered — an error whose
  // message is empty renders nothing, and the control must not describe it.
  useRegisterFormSlot('message', !!body);

  if (!body) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn('text-sm font-medium text-destructive', className)}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessage.displayName = 'FormMessage';

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
};
